import React from "react";

/**
 * PhaseIndicator — cycles through a list of status strings on a timer so
 * long-running (5-60s) async actions feel dynamic instead of a frozen
 * spinner. Purely cosmetic; no backend progress stream is required.
 *
 * Props:
 *   phases       string[]  Required. The status strings to rotate through.
 *   intervalMs   number    Optional. Rotation cadence. Default 1600ms.
 *   variant      string    Optional. "dots" (default) shows 3 pulsing
 *                          amber dots before the text. "text" is just the
 *                          rotating string (used inside dark modals that
 *                          already have their own spinner).
 *   testid       string    Optional. data-testid on the root span.
 *   className    string    Optional. Extra classes for the root span.
 */
export default function PhaseIndicator({
  phases,
  intervalMs = 1600,
  variant = "dots",
  testid,
  className = "",
}) {
  const list = React.useMemo(
    () => (Array.isArray(phases) && phases.length > 0 ? phases : ["Working…"]),
    [phases]
  );
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    if (list.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), intervalMs);
    return () => clearInterval(t);
  }, [list.length, intervalMs]);

  if (variant === "text") {
    return (
      <span
        data-testid={testid}
        className={className}
        aria-live="polite"
      >
        {list[idx]}
      </span>
    );
  }

  return (
    <span
      data-testid={testid}
      className={`text-[11px] italic text-white/55 inline-flex items-center gap-1.5 ${className}`}
      style={{ minWidth: 200 }}
      aria-live="polite"
    >
      <span className="inline-flex items-center gap-0.5">
        <span className="inline-block rounded-full animate-pulse"
          style={{ width: 4, height: 4, backgroundColor: "#F59E0B" }} />
        <span className="inline-block rounded-full animate-pulse"
          style={{ width: 4, height: 4, backgroundColor: "#F59E0B", animationDelay: "200ms" }} />
        <span className="inline-block rounded-full animate-pulse"
          style={{ width: 4, height: 4, backgroundColor: "#F59E0B", animationDelay: "400ms" }} />
      </span>
      <span>{list[idx]}</span>
    </span>
  );
}
